import { AxiosResponse } from "axios";

import {
  FixedTranslationsLanguageCodes,
  ILanguageResponse,
  ITranslationResponse,
} from "../@types/Translations";
import { api } from "./api";

/**
 * EN-5191: how long the portal waits for its own copy before rendering in English. The paint
 * gate in App.tsx blocks on this request, so an unbounded wait is a blank portal.
 */
const TRANSLATIONS_TIMEOUT_MS = 5000;

export const translationService = {
  /**
   * EN-5191: `usedLocation` narrows the list to languages that actually have non-archived
   * translations for that surface. The table itself is a registry of codes, not a statement
   * that anything has been translated into them, so an unfiltered list would offer languages
   * that render as the English fallback — a broken-looking setting rather than a missing
   * translation.
   */
  async getLanguages(
    usedLocation?: string
  ): Promise<AxiosResponse<ILanguageResponse[]>> {
    const serverResponse = await api.get("/Shared/Languages", {
      params: usedLocation ? { usedLocation } : undefined,
    });

    // The response interceptor in api.ts RESOLVES with error.response on a non-2xx rather than
    // rejecting, so without these checks a 500 returns a ProblemDetails object (or "") here,
    // React Query caches it as a successful language list with staleTime: Infinity, and the
    // Settings page then calls .find on it and falls to the error boundary on every visit until
    // a full reload. Only a network error — no response at all — reached the throw below.
    if (
      serverResponse === undefined ||
      serverResponse.status < 200 ||
      serverResponse.status >= 300 ||
      !Array.isArray(serverResponse.data)
    ) {
      throw new Error(
        `GET /Shared/Languages did not return a list (status ${serverResponse?.status}).`
      );
    }

    return serverResponse;
  },

  async getSurveysFixedTranslationsByLanguageCode(
    languageCode: FixedTranslationsLanguageCodes
  ): Promise<AxiosResponse<ITranslationResponse[]>> {
    const url = `/Shared/Languages/${languageCode}/Translations?usedLocation=SurveysFixedTranslations`;

    const serverResponse = await api.get(url);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  // EN-5191: the portal's own UI strings, scoped in the translations table by
  // usedLocation=Portal. Keys are flat and underscore-separated ("login_submit").
  //
  // The response is always a FULL set, whatever languageCode says. Server-side,
  // TranslationRepository.GetAllWithFallbackAsync drives off the en-GB rows and LEFT JOINs the
  // requested language, COALESCEing value and languageCode per key — so a key with no row in the
  // requested language comes back as its en-GB row. Never infer "this language is fully
  // translated" from the response being complete; read each entry's languageCode.
  //
  // That also means an all-English response is ambiguous, and one cause is easy to miss: the
  // service resolves the code through LanguageRepository.FindByCodeAsync FIRST and falls back to
  // en-GB when there is no matching row in public."Languages". A language with translations
  // seeded but no Languages row answers entirely in English, with a 200 and no error.
  async getPortalTranslationsByLanguageCode(
    languageCode: string
  ): Promise<AxiosResponse<ITranslationResponse[]>> {
    const url = `/Shared/Languages/${languageCode}/Translations`;

    const serverResponse = await api.get(url, {
      params: {
        usedLocation: "Portal",
      },
      // The gate in App.tsx holds the first paint until this resolves, and the axios instance
      // has no timeout of its own, so an API that hangs rather than refusing would keep a
      // signed-in non-English user on a spinner for as long as the ALB allowed. Five seconds
      // then English, which is what the portal did before the gate existed.
      timeout: TRANSLATIONS_TIMEOUT_MS,
    });

    // Same reason as getLanguages: the interceptor resolves with error.response on a non-2xx,
    // so without this a ProblemDetails body would be parsed as a translation list and merged
    // into the i18next store.
    if (
      serverResponse === undefined ||
      serverResponse.status < 200 ||
      serverResponse.status >= 300 ||
      !Array.isArray(serverResponse.data)
    ) {
      throw new Error(
        `GET ${url} did not return a list (status ${serverResponse?.status}).`
      );
    }

    return serverResponse;
  },

  async getArticlesFixedTranslationsByLanguageCode(
    languageCode: FixedTranslationsLanguageCodes
  ): Promise<AxiosResponse<ITranslationResponse[]>> {
    const url = `/Shared/Languages/${languageCode}/Translations`;

    const serverResponse = await api.get(url, {
      params: {
        usedLocation: "ArticlesFixedTranslations",
      },
    });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },
};
